import { useAtom } from 'jotai';
import styled from 'styled-components';

import {
  Accordion,
  AccordionSection,
  Action,
  ButtonGroup,
  Text,
  ActionCard,
  breakpoints,
  Size,
  Skeleton,
} from '@soluto-private/mx-asurion-ui-react';

import { type Actions as BumperActions } from '@automation-suite/shared-clients-widget-variant-bumper';
import { type Actions as FacActions } from '@automation-suite/shared-clients-widget-variant-fac';
import { type Actions as EmbeddedActions } from '@automation-suite/shared-clients-widget-variant-embedded';
import { useAsyncAtom } from '@automation-suite/shared-utils-jotai';

import { ContentfulIntegration, OptionEditor, ScriptIntegration, SdkIntegration } from './components';
import { widgetAtom, widgetParamsAtom } from './storage';
import { Variant } from '@automation-suite/widget-client/lib/package';
import { ThemeConfigPreview } from './components/ThemeConfigPreview';

const PageContainer = styled.div`
  margin: 2rem 1rem;

  ${breakpoints.md} {
    margin: 2rem 2rem;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 2rem 1rem;

  ${breakpoints.md} {
    max-width: ${Size.MD2}px;
    margin: 2rem auto;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ActionCardContainer = styled.div``;

const EmbeddedWidgetContainer = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 800px;
  overflow: hidden;
  border: 1px solid #eeedf1;
  border-radius: 0.5em;
`;

const Loader = styled(Skeleton)`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const bumperQuickActions = [
  'Get help setting up my device',
  'Get a guided tour of my device',
  'Connect my device to Wi-Fi',
  'Install a software update',
  'Set up parental controls',
];

const facQuickActions = ['Activate my phone', 'Transfer data', 'Restore contacts', 'Back up my photos'];

const embeddedQuickActions = ['Get help setting up my device', 'Get a guided tour of my device'];

const widgetMap = {
  production: [
    { id: 'verizon_device_onboarding_si', displayName: 'Verizon V2', variant: Variant.FLOATING_ACTION_CARD },
    { id: 'tt_nextgen', displayName: 'Asurion', variant: Variant.FLOATING_ACTION_CARD },
    { id: 'mcafee_bot', displayName: 'McAfee Demo', variant: Variant.FLOATING_ACTION_CARD },
    { id: 'authenticated_joyful', displayName: 'Joyful (authenticated)', variant: Variant.FLOATING_ACTION_CARD },
    { id: 'unauthenticated_joyful', displayName: 'Joyful (unauthenticated)', variant: Variant.FLOATING_ACTION_CARD },
    { id: 'si-ee', displayName: 'EE', variant: Variant.BUMPER },
  ],
  development: [
    { id: 'si-verizon', displayName: 'Verizon V2', variant: Variant.FLOATING_ACTION_CARD },
    { id: 'si-ee', displayName: 'EE', variant: Variant.BUMPER },
    { id: 'mcafee_demo_dev', displayName: 'McAfee Demo', variant: Variant.FLOATING_ACTION_CARD },
  ],
  qa: [
    { id: 'simplr_twilio_flex', displayName: 'Simplr Twilio Flex', variant: Variant.FLOATING_ACTION_CARD },
    { id: 'mcafee_stg', displayName: 'McAfee Staging', variant: Variant.FLOATING_ACTION_CARD },
  ],
};

const widgets =
  process.env.NX_PUBLIC_APP_ENV && process.env.NX_PUBLIC_APP_ENV in widgetMap
    ? widgetMap[process.env.NX_PUBLIC_APP_ENV as keyof typeof widgetMap]
    : [];

export const WidgetDocs = () => {
  const { value: widget } = useAsyncAtom(widgetAtom);
  const [widgetParams, setWidgetParams] = useAtom(widgetParamsAtom);

  return (
    <PageContainer>
      <Container>
        <Text size={7} as="h1" weight="heavy">
          Service Initiation Widget
        </Text>
        <Text size={4} as="h2" weight="feather">
          The service-initiation widget was created to quickly initiate services for our many partners. Made in mind to
          easily and consistently provide tech-support across all our sites.
        </Text>
        <ContentContainer>
          <Text size={4} as="span" weight="heavy">
            Important links
          </Text>

          <ButtonGroup>
            <Action
              href="https://www.figma.com/file/pcUzO5gkzold9dxy91zgSS/Chat-Experiences?type=design&node-id=2251%3A6162&mode=design&t=2D75Nd35Fe4IVwzn-1"
              target="_blank"
              variant="flat"
            >
              Figma files
            </Action>
            <Action href="https://asurion.enterprise.slack.com/archives/C03TA6NET7F" target="_blank" variant="flat">
              Find us on slack!
            </Action>
          </ButtonGroup>
        </ContentContainer>
        <div id="parent-element" />
        {widgetParams?.variant === 'embedded' && (
          <EmbeddedWidgetContainer id="embedded-widget-placeholder">
            <Loader height={'100%'} />
          </EmbeddedWidgetContainer>
        )}
        <ContentContainer>
          {widgets.length > 0 && (
            <>
              <Text size={4}>Widget Shortcuts</Text>
              <ButtonGroup>
                {widgets.map((widget) => (
                  <Action
                    key={widget.id}
                    data-testid={`widget-${widget.id}-button`}
                    onClick={() => {
                      setWidgetParams({
                        id: widget.id,
                        variant: widget.variant,
                      });
                    }}
                    style={{ backgroundColor: 'black' }}
                  >
                    {widget.displayName}
                  </Action>
                ))}
              </ButtonGroup>
            </>
          )}
          {widgetParams?.id && (
            <>
              <Text size={3}>Actions</Text>
              <ButtonGroup>
                {widgetParams?.variant === Variant.BUMPER && (
                  <>
                    <Action color={'primary'} onClick={(widget as BumperActions)?.expand}>
                      Expand
                    </Action>
                    <Action color={'primary'} onClick={(widget as BumperActions)?.collapse}>
                      Collapse
                    </Action>
                  </>
                )}
                {widgetParams?.variant === Variant.FLOATING_ACTION_CARD && (
                  <>
                    <Action color={'primary'} onClick={(widget as FacActions)?.DisplayButton}>
                      Display Button
                    </Action>
                    <Action color={'primary'} onClick={(widget as FacActions)?.DisplayCard}>
                      Display Card
                    </Action>
                    <Action color={'primary'} onClick={(widget as FacActions)?.DisplayWindow}>
                      Display Window
                    </Action>
                  </>
                )}
              </ButtonGroup>
              {widgetParams?.variant === Variant.BUMPER && (
                <>
                  <Text size={3}>Deep Links</Text>
                  <ButtonGroup>
                    {bumperQuickActions.map((action) => (
                      <ActionCardContainer key={`action-${action}`}>
                        <ActionCard
                          variant="button"
                          onClick={() =>
                            (widget as BumperActions)?.initiateChat({
                              message: action,
                            })
                          }
                        >
                          {action}
                        </ActionCard>
                      </ActionCardContainer>
                    ))}
                  </ButtonGroup>
                </>
              )}
              {widgetParams?.variant === Variant.FLOATING_ACTION_CARD && (
                <>
                  <Text size={3}>Deep Links</Text>
                  <ButtonGroup>
                    {facQuickActions.map((message) => (
                      <ActionCardContainer key={`action-${message}`}>
                        <ActionCard
                          variant="button"
                          onClick={() =>
                            (widget as FacActions)?.initiateSimplrChat({
                              message,
                            })
                          }
                        >
                          {message}
                        </ActionCard>
                      </ActionCardContainer>
                    ))}
                  </ButtonGroup>
                </>
              )}
              {widgetParams?.variant === Variant.EMBEDDED && (
                <>
                  <Text size={3}>Deep Links</Text>
                  <ButtonGroup>
                    {embeddedQuickActions.map((message) => (
                      <ActionCardContainer key={`action-${message}`}>
                        <ActionCard
                          variant="button"
                          onClick={() =>
                            (widget as EmbeddedActions)?.initiateSimplrChat({
                              message,
                            })
                          }
                        >
                          {message}
                        </ActionCard>
                      </ActionCardContainer>
                    ))}
                  </ButtonGroup>
                </>
              )}
            </>
          )}

          <Text size={4}>Loader Props</Text>
          <OptionEditor />
        </ContentContainer>
        <ContentContainer>
          <Text size={4}>Full Variant Option Overrides</Text>
          <Text as="p">
            The full list of options for the <i>variant option overrides</i> property
          </Text>
          <ThemeConfigPreview />
        </ContentContainer>
        <ContentContainer>
          <Text size={4}>How do I integrate with the widget?</Text>
          <Text as="p">
            Integrating with the widget is as easy as dropping a script into the host app or configuring a new content
            model in Contentful{' '}
            <span aria-label="curtis-fave-emoji" role="img">
              😎
            </span>
          </Text>
          <Accordion numbered={true} variant="full-page">
            <AccordionSection header="Integrating in Contentful (only on asurion.com)">
              <ContentfulIntegration />
            </AccordionSection>
            <AccordionSection header="Using the SDK" tabIndex={-1}>
              <SdkIntegration />
            </AccordionSection>
            <AccordionSection header="Integrating with a script tag" tabIndex={-1}>
              <ScriptIntegration />
            </AccordionSection>
          </Accordion>
        </ContentContainer>
      </Container>
    </PageContainer>
  );
};
